/** @jsxImportSource preact */
import { h } from 'preact';
import { useState, useMemo } from 'preact/hooks';
import { Modal } from './Modal';
import { ImpactPill } from './NatSecIncidentDetailModal';
import { formatTaxonomyLabel } from '../lib/taxonomyFormat';

export interface IncidentListItem {
  incidentId: string;
  title: string;
  date: string;
  subdomain: string;
  highestSeverityScore: number | null;
  /** The direct rating in ONE harm category, when the list is that
   * category's (a harm severity scale cell); the table shows it in place
   * of the highest-across-categories column, under `severityColumn`. */
  severityScore?: number | null;
  // National Security Incident View rows carry these instead of the
  // subdomain / highest-severity columns. Optional so the type continues
  // to match the standard incident list payload.
  natSecResponseLevel?: string;
  natSecOverallScore?: number | null;
  /** One-line subject-system summary (Arcola mirror), e.g. "ChatGPT 4o
   * Mini (OpenAI)"; "Unidentified" / "None named" for the evidenced empty
   * states. Absent on datasets without identity cells. */
  aiSystem?: string;
}

export interface ActiveFilter {
  label: string;
  values: string[];
}

interface IncidentListModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  items: IncidentListItem[];
  loading: boolean;
  onIncidentClick: (incidentId: string) => void;
  activeFilters?: ActiveFilter[];
  /** Head the severity column "Severity — <label>" and fill it from each
   * item's `severityScore` (one harm category's direct rating) instead of
   * "Highest Severity" from `highestSeverityScore`. */
  severityColumn?: { label: string };
}

type SortField = 'incidentId' | 'title' | 'date' | 'subdomain' | 'highestSeverityScore';

const parseNumericId = (id: string): number => {
  const n = parseInt(id, 10);
  return isNaN(n) ? Infinity : n;
};

/**
 * Parse an incident date for sort comparison. Dates may come in as
 * "DD/MM/YYYY", "YYYY-MM-DD", "Month YYYY", or any free-form string —
 * Date.parse handles most ISO/US forms; for "DD/MM/YYYY" we reshape first
 * so months and days don't get swapped.
 */
function parseDateForSort(raw: string): number {
  if (!raw) return Number.POSITIVE_INFINITY;
  const dmy = raw.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return Date.UTC(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
  }
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}

/**
 * The sortable incident table with its filter chips, without the modal
 * around it, so a richer modal (the harm severity scale's cell detail)
 * can place it under its own content. IncidentListModal is this inside a
 * Modal, unchanged for the chart click-throughs.
 */
export function IncidentListTable({
  items,
  loading,
  onIncidentClick,
  activeFilters,
  severityColumn,
}: Pick<IncidentListModalProps, 'items' | 'loading' | 'onIncidentClick' | 'activeFilters' | 'severityColumn'>) {
  // Date descending: the modal opens on the most recent incidents.
  // Undated rows sort last in either direction (see the date branch).
  const [sortBy, setSortBy] = useState<SortField>('date');
  const severityOf = (it: IncidentListItem): number | null =>
    severityColumn ? it.severityScore ?? null : it.highestSeverityScore;
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleClick = (e: MouseEvent, incidentId: string) => {
    e.preventDefault();
    onIncidentClick(incidentId);
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field: SortField) =>
    sortBy === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp: number;
      switch (sortBy) {
        case 'incidentId':
          cmp = parseNumericId(a.incidentId) - parseNumericId(b.incidentId);
          break;
        case 'highestSeverityScore':
          cmp = (severityOf(a) ?? -1) - (severityOf(b) ?? -1);
          break;
        case 'date': {
          const at = parseDateForSort(a.date);
          const bt = parseDateForSort(b.date);
          // Undated rows (parse -> +Infinity) stay LAST in either
          // direction, instead of floating to the top when descending.
          if (at === Number.POSITIVE_INFINITY || bt === Number.POSITIVE_INFINITY) {
            return at === bt ? 0 : at === Number.POSITIVE_INFINITY ? 1 : -1;
          }
          cmp = at - bt;
          break;
        }
        default: {
          const aVal = String(a[sortBy] ?? '').toLowerCase();
          const bVal = String(b[sortBy] ?? '').toLowerCase();
          cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        }
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, sortBy, sortDir, severityColumn?.label]);

  const hasFilters = activeFilters && activeFilters.length > 0;

  const thBase = {
    textAlign: 'left' as const,
    padding: '6px 8px 6px 0',
    borderBottom: '1px solid var(--gc-border, #e5e7eb)',
    fontWeight: 600,
    cursor: 'pointer' as const,
    userSelect: 'none' as const,
  };
  const tdBase = {
    padding: '6px 8px 6px 0',
    borderBottom: '1px solid var(--gc-border, #e5e7eb)',
    verticalAlign: 'top' as const,
  };

  return (
    <div>
      {hasFilters && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          paddingBottom: 12,
          marginBottom: 4,
          borderBottom: '1px solid var(--gc-border, #e5e7eb)',
        }}>
          {activeFilters.map((filter) => (
            <span
              key={filter.label}
              style={{
                display: 'inline-block',
                fontSize: 11,
                padding: '3px 8px',
                borderRadius: 12,
                backgroundColor: 'var(--gc-badge-bg, #f3f4f6)',
                color: 'var(--gc-badge-text, #374151)',
                border: '1px solid var(--gc-border, #e5e7eb)',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ fontWeight: 600 }}>{filter.label}:</span>{' '}
              {filter.values.join(', ')}
            </span>
          ))}
        </div>
      )}
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--gc-text-muted, #6b7280)' }}>
          Loading incidents...
        </div>
      ) : sortedItems.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--gc-text-muted, #6b7280)' }}>
          No incidents found.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th onClick={() => handleSort('incidentId')} style={{ ...thBase, width: 60 }}>
                ID{sortIndicator('incidentId')}
              </th>
              <th onClick={() => handleSort('title')} style={thBase}>
                Title{sortIndicator('title')}
              </th>
              <th onClick={() => handleSort('date')} style={{ ...thBase, width: 100 }}>
                Incident Date{sortIndicator('date')}
              </th>
              <th
                onClick={() => handleSort('subdomain')}
                style={{ ...thBase, width: 220 }}
                title="Click into incidents to see additional subdomains associated with each incident"
              >
                Primary Risk Subdomain{sortIndicator('subdomain')}
              </th>
              <th
                onClick={() => handleSort('highestSeverityScore')}
                style={{ ...thBase, width: 130, textAlign: 'center' }}
                title={severityColumn ? `The direct severity rating for ${severityColumn.label}` : 'The severity of the harm category with the highest rating'}
              >
                {severityColumn ? `Severity — ${severityColumn.label}` : 'Highest Severity'}{sortIndicator('highestSeverityScore')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((it) => (
              <tr key={it.incidentId} style={{ cursor: 'pointer' }}>
                <td style={{ ...tdBase, color: 'var(--gc-link, inherit)', fontWeight: 500 }}>
                  <a href="#" onClick={(e) => handleClick(e as unknown as MouseEvent, it.incidentId)} style={{ color: 'inherit', textDecoration: 'none' }}>
                    {it.incidentId}
                  </a>
                </td>
                <td style={tdBase}>
                  <a href="#" onClick={(e) => handleClick(e as unknown as MouseEvent, it.incidentId)} style={{ color: 'inherit', textDecoration: 'none' }}>
                    {it.title || '—'}
                  </a>
                </td>
                <td style={{ ...tdBase, color: 'var(--gc-text-muted, #6b7280)', whiteSpace: 'nowrap' }}>
                  {it.date || '—'}
                </td>
                <td style={{ ...tdBase, color: 'var(--gc-text-muted, #6b7280)' }}>
                  {it.subdomain ? formatTaxonomyLabel(it.subdomain) : '—'}
                </td>
                <td style={{ ...tdBase, textAlign: 'center' }}>
                  <ImpactPill score={severityOf(it)} variant="standard" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function IncidentListModal({ isOpen, onClose, title, items, loading, onIncidentClick, activeFilters }: IncidentListModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <IncidentListTable items={items} loading={loading} onIncidentClick={onIncidentClick} activeFilters={activeFilters} />
    </Modal>
  );
}
